import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { HIGHLIGHT_STYLE_OPTIONS, HIGHLIGHT_COLOR_OPTIONS } from '../../../../utils/constants'
import { Box, Text, Button, Icon } from '@deseretbook/react-ui'
import HighlightOptions from './HighlightOptions'

/**
 * `AnnotationActionMenu` view for displaying and managing an annotation's highlight.
 * This component handles editing, and deleting a highlight. It should
 * be used as a child of `AnnotationActionMenu`.
 */
class HighlightView extends Component {
  /**
   * Component constructor.
   */
  constructor() {
    super()

    this.state = {
      /** The value of the button being saved so we know how to display loading state. */
      activeButton: null,
    }

    this.onClickHandler = this.onClickHandler.bind(this)
    this.renderStyles = this.renderStyles.bind(this)
  }

  /**
   * `onClick` handler for buttons that change style and color of the highlight.
   *
   * @param {string} key - key of highlight value to be changed.
   * @param {string} value - changed value for highlight
   */
  onClickHandler(key, value) {
    const { highlight, onSave, isLoading } = this.props

    // Don't make the change if we're in limbo or if the new value is the current value.
    if (isLoading || highlight[key] === value) {
      return
    }

    this.setState(
      {
        activeButton: value,
      },
      () => onSave({ [key]: value }),
    )
  }

  /**
   * Renders style picker options for editing the highlight.
   *
   * @returns {function} Component
   */
  renderStyles() {
    const { highlight, isLoading } = this.props
    const { activeButton } = this.state

    // Figure out which color to use for the style buttons.
    const displayColor = HIGHLIGHT_COLOR_OPTIONS.reduce((active, color) =>
      // Pick either the first color, or the one that is selected.
      !active || highlight.color === color.value ? color : active,
    )

    return HIGHLIGHT_STYLE_OPTIONS.map((style) => {
      const isActive = style.value === highlight.style
      const showLoading = isLoading && style.value === activeButton

      return (
        <Box className="style-option-container">
          <Button
            isStatic={isActive || showLoading}
            isLoading={showLoading}
            title={style.label}
            onClick={() => this.onClickHandler('style', style.value)}
            style={{ color: displayColor.hex }}
            className={`button-${style.label.toLowerCase()}`}
          >
            <Text className="style-letters">Aa</Text>
          </Button>
          {!!isActive && <Icon name="upSelected" className="style-selected" />}
        </Box>
      )
    })
  }

  /**
   * Determines how to render the component.
   *
   * @returns {function} Component
   */
  render() {
    const { activeButton } = this.state
    const { highlight, onSave, onBack, className, isLoading, ...rest } = this.props
    const classes = classNames('HighlightView', className)

    return (
      <div className={classes} {...rest}>
        <Box isShadowless padding={0}>
          <Text className="heading">Highlight Color</Text>
          <Box className="colors">
            <HighlightOptions
              highlight={highlight}
              isLoading={isLoading}
              activeButton={activeButton}
              onClick={this.onClickHandler}
            />
          </Box>
          <Box
            className="style-container"
            alignItems="start"
            justifyContent="space-between"
            style={{ marginTop: '20px' }}
          >
            <Text className="heading">Highlight Type</Text>
            {this.renderStyles()}
          </Box>
        </Box>
      </div>
    )
  }
}

HighlightView.defaultProps = {
  isLoading: false,
  highlight: {},
  onSave: () => {},
  onBack: () => {},
  className: '',
}

HighlightView.propTypes = {
  /** True if an action is taking place. */
  isLoading: PropTypes.bool,
  /** Annotation highlight object. */
  highlight: PropTypes.shape({}),
  /** Callback for when a note should be saved. */
  onSave: PropTypes.func,
  /** Callback for when the view should be changed back to the previous view. */
  onBack: PropTypes.func,
  /** Additional class name. */
  className: PropTypes.string,
}

export default HighlightView
